import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { LoadingGroup, LoadingSkeleton } from "capitalrx-components-2.0/lib/general";
import { Shelf, Spacing, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold, Title } from "capitalrx-components-2.0/lib/typography";

import { Drug } from "@/App/api/drug";
import { ThemedButton } from "@/App/components";
import { StackOrShelf } from "@/App/components/StackOrShelf";

const TRANSLATION_PATH = "FIND_BEST_PRICE.drugHeader";

interface DrugHeaderProps {
  onBackPressed: () => void;
  isLoading: boolean;
  drug: Drug;
  hideDrugDetails?: boolean;
}

export const DrugHeader = ({ onBackPressed, isLoading, drug, hideDrugDetails }: DrugHeaderProps) => {
  const { t: translate } = useTranslation();
  const [searchParams] = useSearchParams();
  const dosage = searchParams.get("dosage");
  const quantity = searchParams.get("quantity");
  const daysSupply = searchParams.get("days_supply");

  const { drugDetailsSpacing, drugDetailsStackSpacing } = useResponsive({
    drugDetailsSpacing: {
      desktop: "large" as Spacing,
      tablet: "large" as Spacing,
      mobile: "small" as Spacing,
    },
    drugDetailsStackSpacing: {
      desktop: "mini" as Spacing,
      tablet: "mini" as Spacing,
      mobile: "medium" as Spacing,
    },
  });

  return (
    <LoadingGroup loading={isLoading}>
      <Stack spacing="mini">
        <Shelf>
          <ThemedButton testId="go-back" onClick={onBackPressed} appearance="text" icon="arrow-left">
            {translate(`${TRANSLATION_PATH}.back`)}
          </ThemedButton>
          <Shelf.Spacer />
        </Shelf>
        <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ height: 36, width: 120 }} />}>
          <Title testId="drug-name">{drug?.drug_name}</Title>
        </LoadingGroup.Placeholder>
        {!hideDrugDetails && (
          <Stack spacing={drugDetailsStackSpacing}>
            {!!drug && (
              <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ height: 20, width: 60 }} />}>
                <Body>{translate(`${TRANSLATION_PATH}.${drug?.drug_type?.toLowerCase()}`)}</Body>
              </LoadingGroup.Placeholder>
            )}
            <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ height: 24, width: 220 }} />}>
              <StackOrShelf spacing={drugDetailsSpacing}>
                <Body>
                  <Semibold>{translate(`${TRANSLATION_PATH}.dosage`)}: </Semibold>
                  {dosage}
                </Body>
                <Body>
                  <Semibold>{translate(`${TRANSLATION_PATH}.quantity`)}: </Semibold>
                  {quantity}
                </Body>
                <Body>
                  <Semibold>{translate(`${TRANSLATION_PATH}.daysSupply`)}: </Semibold>
                  {daysSupply} {translate(`${TRANSLATION_PATH}.unitDays`, { count: +daysSupply })}
                </Body>
                <Shelf.Spacer />
              </StackOrShelf>
            </LoadingGroup.Placeholder>
          </Stack>
        )}
      </Stack>
      <Shelf.Spacer />
    </LoadingGroup>
  );
};
